import React, { useState } from "react";

import apLocations from "../data/apLocations";
import "./Step2Personal.css";

// ==========================================================
// HELPERS
// ==========================================================

const cleanName = (value) => {
  return value
    .replace(/[^A-Za-z\s]/g, "")
    .replace(/\s+/g, " ");
};

const cleanDigits = (value, maxLength) => {
  return value
    .replace(/[^0-9]/g, "")
    .slice(0, maxLength);
};

// ==========================================================
// VALIDATION
// ==========================================================

const validateName = (value) => {
  return /^[A-Za-z]+(?:\s[A-Za-z]+)*$/.test(
    value.trim()
  );
};

const validateEmail = (value) => {
  // Email is optional
  if (!value.trim()) {
    return true;
  }

  return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(
    value.trim()
  );
};

const validatePinCode = (value) => {
  return /^[1-9][0-9]{5}$/.test(value);
};

const validateMobile = (value) => {
  return /^[6-9][0-9]{9}$/.test(value);
};

const validateAadhaar = (value) => {
  return /^[0-9]{12}$/.test(value);
};

const validatePAN = (value) => {
  return /^[A-Z]{5}[0-9]{4}[A-Z]$/.test(
    value.toUpperCase()
  );
};

// ==========================================================
// COMPONENT
// ==========================================================

function Step2Personal({
  nextStep,
  prevStep,
  formData = {},
  updateFormData,
}) {
  // ========================================================
  // PERSONAL DETAILS
  // ========================================================

  const [fullName, setFullName] = useState(
    formData.name ||
      formData.fullName ||
      formData.memberName ||
      ""
  );

  const [fatherName, setFatherName] = useState(
    formData.fatherName || ""
  );

  const [dateOfBirth, setDateOfBirth] = useState(
    formData.dateOfBirth ||
      formData.dob ||
      ""
  );

  const [gender, setGender] = useState(
    formData.gender || ""
  );

  const [maritalStatus, setMaritalStatus] =
    useState(
      formData.maritalStatus || ""
    );

  const [community, setCommunity] = useState(
    formData.community || ""
  );

  const [occupation, setOccupation] = useState(
    formData.occupation || ""
  );

  // MOBILE NUMBER MOVED TO PERSONAL DETAILS

  const [mobileNumber, setMobileNumber] =
    useState(
      formData.mobileNumber ||
        formData.mobile ||
        ""
    );

  // ========================================================
  // ADDRESS DETAILS
  // ========================================================

  const [state, setState] = useState(
    formData.state || "Andhra Pradesh"
  );

  const [district, setDistrict] = useState(
    formData.district || ""
  );

  const [constituency, setConstituency] =
    useState(
      formData.constituency || ""
    );

  const [mandal, setMandal] = useState(
    formData.mandal || ""
  );

  const [village, setVillage] = useState(
    formData.village ||
      formData.villageTown ||
      ""
  );

  const [pinCode, setPinCode] = useState(
    formData.pinCode ||
      formData.pincode ||
      ""
  );

  const [address, setAddress] = useState(
    formData.address || ""
  );

  // ========================================================
  // OTHER DETAILS
  // ========================================================

  // Email is optional
  const [email, setEmail] = useState(
    formData.email ||
      formData.emailId ||
      ""
  );

  // ========================================================
  // AADHAAR
  // ========================================================

  const [aadhaarNumber, setAadhaarNumber] =
    useState(
      String(
        formData.aadhaarNumber ||
          formData.aadhaar ||
          ""
      )
        .replace(/[^0-9]/g, "")
        .slice(0, 12)
    );

  // ========================================================
  // PAN
  // ========================================================

  const [panNumber, setPanNumber] = useState(
    formData.panNumber ||
      formData.pan ||
      ""
  );

  const [existingMember] =
    useState(
      formData.existingMember || "No"
    );

  // ========================================================
  // ERRORS
  // ========================================================

  const [errors, setErrors] = useState({});

  // ========================================================
  // LOCATION DATA
  // ========================================================

  const states = Object.keys(
    apLocations || {}
  );

  const districts = state
    ? Object.keys(
        apLocations?.[state] || {}
      )
    : [];

  const constituencies =
    state && district
      ? apLocations?.[state]?.[district] || []
      : [];

  // ========================================================
  // STATE CHANGE
  // ========================================================

  const handleStateChange = (e) => {
    const value = e.target.value;

    setState(value);
    setDistrict("");
    setConstituency("");
    setMandal("");

    setErrors((previous) => ({
      ...previous,
      state: "",
      district: "",
      constituency: "",
      mandal: "",
    }));
  };

  // ========================================================
  // DISTRICT CHANGE
  // ========================================================

  const handleDistrictChange = (e) => {
    const value = e.target.value;

    setDistrict(value);
    setConstituency("");
    setMandal("");

    setErrors((previous) => ({
      ...previous,
      district: "",
      constituency: "",
      mandal: "",
    }));
  };

  // ========================================================
  // FULL NAME
  // ========================================================

  const handleFullNameChange = (e) => {
    const value = cleanName(
      e.target.value
    );

    setFullName(value);

    setErrors((previous) => ({
      ...previous,
      fullName: "",
    }));
  };

  // ========================================================
  // FATHER / HUSBAND NAME
  // ========================================================

  const handleFatherNameChange = (e) => {
    const value = cleanName(
      e.target.value
    );

    setFatherName(value);

    setErrors((previous) => ({
      ...previous,
      fatherName: "",
    }));
  };

  // ========================================================
  // MOBILE
  // ONLY 10 DIGITS
  // ========================================================

  const handleMobileChange = (e) => {
    const value = cleanDigits(
      e.target.value,
      10
    );

    setMobileNumber(value);

    setErrors((previous) => ({
      ...previous,
      mobileNumber: "",
    }));
  };

  // ========================================================
  // MANDAL
  // ONLY LETTERS AND SPACES
  // ========================================================

  const handleMandalChange = (e) => {
    const value = e.target.value
      .replace(/[^A-Za-z\s]/g, "")
      .replace(/\s+/g, " ");

    setMandal(value);

    setErrors((previous) => ({
      ...previous,
      mandal: "",
    }));
  };

  // ========================================================
  // VILLAGE / TOWN
  // ONLY LETTERS AND SPACES
  // ========================================================

  const handleVillageChange = (e) => {
    const value = e.target.value
      .replace(/[^A-Za-z\s]/g, "")
      .replace(/\s+/g, " ");

    setVillage(value);

    setErrors((previous) => ({
      ...previous,
      village: "",
    }));
  };

  // ========================================================
  // PIN CODE
  // ONLY 6 DIGITS
  // ========================================================

  const handlePinCodeChange = (e) => {
    const value = cleanDigits(
      e.target.value,
      6
    );

    setPinCode(value);

    setErrors((previous) => ({
      ...previous,
      pinCode: "",
    }));
  };

  // ========================================================
  // AADHAAR
  // ONLY DIGITS
  // NO SPACES
  // MAXIMUM 12 DIGITS
  // ========================================================

  const handleAadhaarChange = (e) => {
    const value = e.target.value
      .replace(/[^0-9]/g, "")
      .slice(0, 12);

    setAadhaarNumber(value);

    setErrors((previous) => ({
      ...previous,
      aadhaarNumber: "",
    }));
  };

  // ========================================================
  // PAN
  // ONLY LETTERS + NUMBERS
  // AUTOMATIC UPPERCASE
  // MAXIMUM 10 CHARACTERS
  // ========================================================

  const handlePANChange = (e) => {
    const value = e.target.value
      .replace(/[^A-Za-z0-9]/g, "")
      .toUpperCase()
      .slice(0, 10);

    setPanNumber(value);

    setErrors((previous) => ({
      ...previous,
      panNumber: "",
    }));
  };

  // ========================================================
  // VALIDATE FORM
  // ========================================================

  const validateForm = () => {
    const newErrors = {};

    // ------------------------------------------------------
    // FULL NAME
    // ------------------------------------------------------

    if (!fullName.trim()) {
      newErrors.fullName =
        "Full Name is required.";
    } else if (!validateName(fullName)) {
      newErrors.fullName =
        "Full Name must contain only letters and spaces.";
    }

    // ------------------------------------------------------
    // FATHER / HUSBAND NAME
    // ------------------------------------------------------

    if (!fatherName.trim()) {
      newErrors.fatherName =
        "Father / Husband Name is required.";
    } else if (!validateName(fatherName)) {
      newErrors.fatherName =
        "Father / Husband Name must contain only letters and spaces.";
    }

    // ------------------------------------------------------
    // DATE OF BIRTH
    // ------------------------------------------------------

    if (!dateOfBirth) {
      newErrors.dateOfBirth =
        "Date of Birth is required.";
    } else {
      const selectedDate =
        new Date(dateOfBirth);

      const todayDate =
        new Date();

      todayDate.setHours(
        0,
        0,
        0,
        0
      );

      if (
        selectedDate >
        todayDate
      ) {
        newErrors.dateOfBirth =
          "Date of Birth cannot be in the future.";
      }
    }

    // ------------------------------------------------------
    // GENDER
    // ------------------------------------------------------

    if (!gender) {
      newErrors.gender =
        "Please select Gender.";
    }

    // ------------------------------------------------------
    // MARITAL STATUS
    // ------------------------------------------------------

    if (!maritalStatus) {
      newErrors.maritalStatus =
        "Please select Marital Status.";
    }

    // ------------------------------------------------------
    // COMMUNITY
    // ------------------------------------------------------

    if (!community) {
      newErrors.community =
        "Please select Community.";
    }

    // ------------------------------------------------------
    // OCCUPATION
    // ------------------------------------------------------

    if (!occupation) {
      newErrors.occupation =
        "Please select Occupation.";
    }

    // ------------------------------------------------------
    // MOBILE
    // ------------------------------------------------------

    if (!mobileNumber) {
      newErrors.mobileNumber =
        "Mobile Number is required.";
    } else if (
      !validateMobile(
        mobileNumber
      )
    ) {
      newErrors.mobileNumber =
        "Enter a valid 10-digit mobile number.";
    }

    // ------------------------------------------------------
    // STATE
    // ------------------------------------------------------

    if (!state) {
      newErrors.state =
        "Please select State.";
    }

    // ------------------------------------------------------
    // DISTRICT
    // ------------------------------------------------------

    if (!district) {
      newErrors.district =
        "Please select District.";
    }

    // ------------------------------------------------------
    // CONSTITUENCY
    // ------------------------------------------------------

    if (!constituency) {
      newErrors.constituency =
        "Please select Constituency.";
    }

    // ------------------------------------------------------
    // MANDAL
    // ------------------------------------------------------

    if (!mandal.trim()) {
      newErrors.mandal =
        "Mandal is required.";
    } else if (
      !/^[A-Za-z]+(?:\s[A-Za-z]+)*$/.test(
        mandal.trim()
      )
    ) {
      newErrors.mandal =
        "Mandal must contain only letters and spaces.";
    }

    // ------------------------------------------------------
    // VILLAGE
    // ------------------------------------------------------

    if (!village.trim()) {
      newErrors.village =
        "Village / Town is required.";
    } else if (
      !/^[A-Za-z]+(?:\s[A-Za-z]+)*$/.test(
        village.trim()
      )
    ) {
      newErrors.village =
        "Village / Town must contain only letters and spaces.";
    }

    // ------------------------------------------------------
    // PIN CODE
    // ------------------------------------------------------

    if (!pinCode) {
      newErrors.pinCode =
        "PIN Code is required.";
    } else if (
      !validatePinCode(pinCode)
    ) {
      newErrors.pinCode =
        "Enter a valid 6-digit PIN Code.";
    }

    // ------------------------------------------------------
    // ADDRESS
    // ------------------------------------------------------

    if (!address.trim()) {
      newErrors.address =
        "Address is required.";
    }

    // ------------------------------------------------------
    // EMAIL
    // OPTIONAL
    // ------------------------------------------------------

    if (!validateEmail(email)) {
      newErrors.email =
        "Enter a valid email address.";
    }

    // ------------------------------------------------------
// AADHAAR — MANDATORY, EXACTLY 12 DIGITS
// ------------------------------------------------------

if (!aadhaarNumber.trim()) {
  newErrors.aadhaarNumber =
    "Aadhaar Number is required.";
} else if (!validateAadhaar(aadhaarNumber.trim())) {
  newErrors.aadhaarNumber =
    "Aadhaar Number must contain exactly 12 digits.";
}

  // ------------------------------------------------------
// PAN — MANDATORY, VALID FORMAT
// ------------------------------------------------------

const cleanPAN = panNumber.trim().toUpperCase();

if (!cleanPAN) {
  newErrors.panNumber =
    "PAN Number is required.";
} else if (!validatePAN(cleanPAN)) {
  newErrors.panNumber =
    "Enter a valid PAN Number, e.g. ABCDE1234F.";
}

    setErrors(
      newErrors
    );

    return (
      Object.keys(
        newErrors
      ).length === 0
    );
  };

  // ========================================================
  // NEXT BUTTON
  // ========================================================

  const handleNext = () => {
    const isValid =
      validateForm();

    if (!isValid) {
      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });

      return;
    }

    // ------------------------------------------------------
    // CLEAN VALUES
    // ------------------------------------------------------

    const cleanFullName =
      fullName.trim();

    const cleanFatherName =
      fatherName.trim();

    const cleanMandal =
      mandal.trim();

    const cleanVillage =
      village.trim();

    const cleanAddress =
      address.trim();

    const cleanEmail =
      email.trim();

    const cleanPAN =
      panNumber
        .trim()
        .toUpperCase();

    // ------------------------------------------------------
    // FINAL DATA
    // ------------------------------------------------------

    const data = {
      ...formData,

      // ====================================================
      // PERSONAL
      // ====================================================

      name:
        cleanFullName,

      fullName:
        cleanFullName,

      memberName:
        cleanFullName,

      fatherName:
        cleanFatherName,

      dateOfBirth,

      dob:
        dateOfBirth,

      gender,

      maritalStatus,

      community,

      occupation,

      // ====================================================
      // MOBILE NUMBER
      // NOW PART OF PERSONAL DETAILS
      // ====================================================

      mobileNumber,

      mobile:
        mobileNumber,

      // ====================================================
      // ADDRESS
      // ====================================================

      state,

      district,

      constituency,

      mandal:
        cleanMandal,

      village:
        cleanVillage,

      villageTown:
        cleanVillage,

      pinCode,

      pincode:
        pinCode,

      address:
        cleanAddress,

      // ====================================================
      // OTHER DETAILS
      // ====================================================

      email:
        cleanEmail,

      emailId:
        cleanEmail,

      // ====================================================
      // AADHAAR
      // ====================================================

      aadhaarNumber:
        aadhaarNumber,

      aadhaar:
        aadhaarNumber,

      // ====================================================
      // PAN
      // ====================================================

      panNumber:
        cleanPAN,

      pan:
        cleanPAN,

      existingMember,
    };

    // ------------------------------------------------------
    // UPDATE PARENT
    // ------------------------------------------------------

    if (
      typeof updateFormData ===
      "function"
    ) {
      updateFormData(
        data
      );
    }

    // ------------------------------------------------------
    // SAVE LOCAL STORAGE
    // ------------------------------------------------------

    try {
      const oldData =
        JSON.parse(
          localStorage.getItem(
            "registrationData"
          ) || "{}"
        );

      const finalData = {
        ...oldData,
        ...data,
      };

      localStorage.setItem(
        "registrationData",
        JSON.stringify(
          finalData
        )
      );
    } catch (error) {
      console.error(
        "Registration data save error:",
        error
      );
    }

    // ------------------------------------------------------
    // GO TO NEXT STEP
    // ------------------------------------------------------

    if (
      typeof nextStep ===
      "function"
    ) {
      nextStep(
        data
      );
    } else {
      console.error(
        "Step2Personal: nextStep function is missing."
      );
    }
  };

  // ========================================================
  // ERROR CLASS
  // ========================================================

  const getFieldClass = (
    field
  ) => {
    return errors[field]
      ? "step2-input-error"
      : "";
  };

  // ========================================================
  // TODAY
  // ========================================================

  const today =
    new Date()
      .toISOString()
      .split("T")[0];

  // ========================================================
  // RENDER
  // ========================================================

  return (
    <div className="step2-page">

      <div className="step2-card">

        <div className="step2-form">

          {/* ==================================================
              PERSONAL DETAILS
          ================================================== */}

          <section className="step2-section">

            <h2>
              Personal Details
            </h2>

            <div className="step2-grid">

              {/* FULL NAME */}

              <div className="step2-field">

                <label>
                  Full Name{" "}
                  <span>*</span>
                </label>

                <input
                  type="text"
                  value={fullName}
                  placeholder="Enter full name"
                  className={
                    getFieldClass(
                      "fullName"
                    )
                  }
                  onChange={
                    handleFullNameChange
                  }
                  autoComplete="name"
                />

                {errors.fullName && (
                  <small className="step2-error">
                    {errors.fullName}
                  </small>
                )}

              </div>

              {/* FATHER / HUSBAND NAME */}

              <div className="step2-field">

                <label>
                  Father / Husband Name{" "}
                  <span>*</span>
                </label>

                <input
                  type="text"
                  value={fatherName}
                  placeholder="Enter father / husband name"
                  className={
                    getFieldClass(
                      "fatherName"
                    )
                  }
                  onChange={
                    handleFatherNameChange
                  }
                />

                {errors.fatherName && (
                  <small className="step2-error">
                    {errors.fatherName}
                  </small>
                )}

              </div>

              {/* DATE OF BIRTH */}

              <div className="step2-field">

                <label>
                  Date of Birth{" "}
                  <span>*</span>
                </label>

                <div className="step2-date-input">

                  <input
                    type="date"
                    value={dateOfBirth}
                    max={today}
                    className={
                      getFieldClass(
                        "dateOfBirth"
                      )
                    }
                    onChange={(e) =>
                      setDateOfBirth(
                        e.target.value
                      )
                    }
                  />

                  

                </div>

                {errors.dateOfBirth && (
                  <small className="step2-error">
                    {errors.dateOfBirth}
                  </small>
                )}

              </div>

              {/* GENDER */}

              <div className="step2-field">

                <label>
                  Gender{" "}
                  <span>*</span>
                </label>

                <select
                  value={gender}
                  className={
                    getFieldClass(
                      "gender"
                    )
                  }
                  onChange={(e) =>
                    setGender(
                      e.target.value
                    )
                  }
                >

                  <option value="">
                    Select Gender
                  </option>

                  <option value="Male">
                    Male
                  </option>

                  <option value="Female">
                    Female
                  </option>

                  <option value="Other">
                    Other
                  </option>

                </select>

                {errors.gender && (
                  <small className="step2-error">
                    {errors.gender}
                  </small>
                )}

              </div>

              {/* MARITAL STATUS */}

              <div className="step2-field">

                <label>
                  Marital Status{" "}
                  <span>*</span>
                </label>

                <select
                  value={maritalStatus}
                  className={
                    getFieldClass(
                      "maritalStatus"
                    )
                  }
                  onChange={(e) =>
                    setMaritalStatus(
                      e.target.value
                    )
                  }
                >

                  <option value="">
                    Select Marital Status
                  </option>

                  <option value="Married">
                    Married
                  </option>

                  <option value="Unmarried">
                    Unmarried
                  </option>

                  <option value="Widowed">
                    Widowed
                  </option>

                  <option value="Divorced">
                    Divorced
                  </option>

                </select>

                {errors.maritalStatus && (
                  <small className="step2-error">
                    {errors.maritalStatus}
                  </small>
                )}

              </div>

              {/* COMMUNITY */}

              <div className="step2-field">

                <label>
                  Community{" "}
                  <span>*</span>
                </label>

                <select
                  value={community}
                  className={
                    getFieldClass(
                      "community"
                    )
                  }
                  onChange={(e) =>
                    setCommunity(
                      e.target.value
                    )
                  }
                >

                  <option value="">
                    Select Community
                  </option>

                  <option value="Nayi Brahmin">
                    Nayi Brahmin
                  </option>

                  <option value="Other">
                    Other
                  </option>

                </select>

                {errors.community && (
                  <small className="step2-error">
                    {errors.community}
                  </small>
                )}

              </div>

              {/* OCCUPATION */}

              <div className="step2-field">

                <label>
                  Occupation{" "}
                  <span>*</span>
                </label>

                <select
                  value={occupation}
                  className={
                    getFieldClass(
                      "occupation"
                    )
                  }
                  onChange={(e) =>
                    setOccupation(
                      e.target.value
                    )
                  }
                >

                  <option value="">
                    Select Occupation
                  </option>

                  <option value="Salon Shop">
                    Salon Shop
                  </option>

                  <option value="Business">
                    Business
                  </option>

                  <option value="Employee">
                    Employee
                  </option>

                  <option value="Self Employed">
                    Self Employed
                  </option>

                  <option value="Student">
                    Student
                  </option>

                  <option value="Other">
                    Other
                  </option>

                </select>

                {errors.occupation && (
                  <small className="step2-error">
                    {errors.occupation}
                  </small>
                )}

              </div>

              {/* MOBILE NUMBER */}

              <div className="step2-field">

                <label>
                  Mobile Number{" "}
                  <span>*</span>
                </label>

                <div className="step2-mobile-input">

                  <span>
                    +91
                  </span>

                  <input
                    type="text"
                    inputMode="numeric"
                    value={mobileNumber}
                    maxLength={10}
                    placeholder="Enter mobile number"
                    className={
                      getFieldClass(
                        "mobileNumber"
                      )
                    }
                    onChange={
                      handleMobileChange
                    }
                    autoComplete="tel"
                  />

                </div>

                {errors.mobileNumber && (
                  <small className="step2-error">
                    {errors.mobileNumber}
                  </small>
                )}

              </div>

            </div>

          </section>

          {/* ==================================================
              ADDRESS DETAILS
          ================================================== */}

          <section className="step2-section">

            <h2>
              Address Details
            </h2>

            <div className="step2-grid">

              {/* STATE */}

              <div className="step2-field">

                <label>
                  State{" "}
                  <span>*</span>
                </label>

                <select
                  value={state}
                  className={
                    getFieldClass(
                      "state"
                    )
                  }
                  onChange={
                    handleStateChange
                  }
                >

                  <option value="">
                    Select State
                  </option>

                  {states.map(
                    (item) => (
                      <option
                        key={item}
                        value={item}
                      >
                        {item}
                      </option>
                    )
                  )}

                </select>

                {errors.state && (
                  <small className="step2-error">
                    {errors.state}
                  </small>
                )}

              </div>

              {/* DISTRICT */}

              <div className="step2-field">

                <label>
                  District{" "}
                  <span>*</span>
                </label>

                <select
                  value={district}
                  disabled={!state}
                  className={
                    getFieldClass(
                      "district"
                    )
                  }
                  onChange={
                    handleDistrictChange
                  }
                >

                  <option value="">
                    Select District
                  </option>

                  {districts.map(
                    (item) => (
                      <option
                        key={item}
                        value={item}
                      >
                        {item}
                      </option>
                    )
                  )}

                </select>

                {errors.district && (
                  <small className="step2-error">
                    {errors.district}
                  </small>
                )}

              </div>

              {/* CONSTITUENCY */}

              <div className="step2-field">

                <label>
                  Constituency (Assembly){" "}
                  <span>*</span>
                </label>

                <select
                  value={constituency}
                  disabled={!district}
                  className={
                    getFieldClass(
                      "constituency"
                    )
                  }
                  onChange={(e) =>
                    setConstituency(
                      e.target.value
                    )
                  }
                >

                  <option value="">
                    Select Constituency
                  </option>

                  {constituencies.map(
                    (item) => (
                      <option
                        key={item}
                        value={item}
                      >
                        {item}
                      </option>
                    )
                  )}

                </select>

                {errors.constituency && (
                  <small className="step2-error">
                    {errors.constituency}
                  </small>
                )}

              </div>

              {/* MANDAL */}

              <div className="step2-field">

                <label>
                  Mandal{" "}
                  <span>*</span>
                </label>

                <input
                  type="text"
                  value={mandal}
                  placeholder="Enter mandal"
                  className={
                    getFieldClass(
                      "mandal"
                    )
                  }
                  onChange={
                    handleMandalChange
                  }
                />

                {errors.mandal && (
                  <small className="step2-error">
                    {errors.mandal}
                  </small>
                )}

              </div>

              {/* VILLAGE */}

              <div className="step2-field">

                <label>
                  Village / Town{" "}
                  <span>*</span>
                </label>

                <input
                  type="text"
                  value={village}
                  placeholder="Enter village / town"
                  className={
                    getFieldClass(
                      "village"
                    )
                  }
                  onChange={
                    handleVillageChange
                  }
                />

                {errors.village && (
                  <small className="step2-error">
                    {errors.village}
                  </small>
                )}

              </div>

              {/* PIN */}

              <div className="step2-field">

                <label>
                  PIN Code{" "}
                  <span>*</span>
                </label>

                <input
                  type="text"
                  inputMode="numeric"
                  value={pinCode}
                  maxLength={6}
                  placeholder="Enter 6-digit PIN code"
                  className={
                    getFieldClass(
                      "pinCode"
                    )
                  }
                  onChange={
                    handlePinCodeChange
                  }
                />

                {errors.pinCode && (
                  <small className="step2-error">
                    {errors.pinCode}
                  </small>
                )}

              </div>

              {/* ADDRESS */}

              <div className="step2-field step2-full-width">

                <label>
                  Address{" "}
                  <span>*</span>
                </label>

                <textarea
                  value={address}
                  rows="4"
                  placeholder="Enter complete address"
                  className={
                    getFieldClass(
                      "address"
                    )
                  }
                  onChange={(e) =>
                    setAddress(
                      e.target.value
                    )
                  }
                />

                {errors.address && (
                  <small className="step2-error">
                    {errors.address}
                  </small>
                )}

              </div>

            </div>

          </section>

          {/* ==================================================
              OTHER DETAILS
          ================================================== */}

          <section className="step2-section">

            <h2>
              Other Details
            </h2>

            <div className="step2-grid">

              {/* EMAIL */}

              <div className="step2-field">

                <label>
                  Email ID{" "}

                  <small className="optional-label">
                    (Optional)
                  </small>
                </label>

                <input
                  type="email"
                  value={email}
                  placeholder="Enter email address"
                  className={
                    getFieldClass(
                      "email"
                    )
                  }
                  onChange={(e) =>
                    setEmail(
                      e.target.value
                    )
                  }
                  autoComplete="email"
                />

                {errors.email && (
                  <small className="step2-error">
                    {errors.email}
                  </small>
                )}

              </div>

              {/* AADHAAR */}

              <div className="step2-field">

                <label>
  Aadhaar Number <span>*</span>
</label>

               <input
  type="text"
  inputMode="numeric"
  autoComplete="off"
  maxLength={12}
  value={aadhaarNumber}
  placeholder="Enter 12-digit Aadhaar number"
  required
  className={getFieldClass("aadhaarNumber")}
  onChange={handleAadhaarChange}
/>

                {errors.aadhaarNumber && (
                  <small className="step2-error">
                    {errors.aadhaarNumber}
                  </small>
                )}

              </div>

              {/* PAN */}

              <div className="step2-field">

               <label>
  PAN Number <span>*</span>
</label>

               <input
  type="text"
  value={panNumber}
  maxLength={10}
  placeholder="ABCDE1234F"
  required
  className={getFieldClass("panNumber")}
  onChange={handlePANChange}
/>

                <small className="step2-hint">
                  Format: ABCDE1234F
                </small>

                {errors.panNumber && (
                  <small className="step2-error">
                    {errors.panNumber}
                  </small>
                )}

              </div>

            </div>

          </section>

          {/* ==================================================
              BUTTONS
          ================================================== */}

          <div className="step2-buttons">

            <button
              type="button"
              className="step2-back-button"
              onClick={() => {
                if (
                  typeof prevStep ===
                  "function"
                ) {
                  prevStep();
                }
              }}
            >
              BACK
            </button>

            <button
              type="button"
              className="step2-next-button"
              onClick={
                handleNext
              }
            >
              NEXT
            </button>

          </div>

        </div>

      </div>

    </div>
  );
}

export default Step2Personal;